import './styles/main.css';

import { setupApp } from './app';
import { registerRoute, setNotFoundHandler, initRouter } from './router';
import { markPageReady, setupPreloaderFallback } from './navigation';
import { state, saveSession, clearSession } from './state';
import { apiRequest, ApiRequestError } from './api';
import { parseAuthHash } from './ui/auth';

import { mountPlayers, unmountPlayers } from './pages/players';
import { mountGames, unmountGames } from './pages/games';
import { mountControl, unmountControl } from './pages/control';
import { mountAdmin, unmountAdmin } from './pages/admin';
import { mountAuth, unmountAuth } from './pages/auth';
import { mountResetPassword, unmountResetPassword } from './pages/reset-password';
import { mountConfirmEmail, unmountConfirmEmail } from './pages/confirm-email';
import { mountLeaderboard, unmountLeaderboard } from './pages/leaderboard';
import { mountMaps, unmountMaps } from './pages/maps';
import { mountMods, unmountMods } from './pages/mods';
import { mountStatistics, unmountStatistics } from './pages/statistics';
import { mountTools, unmountTools, mountPredict, unmountPredict } from './pages/tools';
import { mountRandom, unmountRandom } from './pages/tools-random';
import { mountMatch, unmountMatch } from './pages/tools-match';
import { mountSupport, unmountSupport } from './pages/support';
import { mountProfile, unmountProfile } from './pages/profile';
import { mountMyStats, unmountMyStats } from './pages/my-stats';
import { mountArchetypes, unmountArchetypes } from './pages/archetypes';
import { initVisitTracking } from './visits';
import { initRecap } from './recap';

async function bootstrap(): Promise<void> {
    setupPreloaderFallback();

    if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }

    // Ссылки из писем Supabase могут прилететь на любой URL (например, на
    // Site URL-корень, если redirect не в allowlist). Перенаправляем на нужный
    // SPA-роут, сохранив hash с токенами.
    const authHash = parseAuthHash();
    if (authHash.accessToken) {
        const path = window.location.pathname;
        const hash = window.location.hash;
        if (authHash.type === 'recovery' && path !== '/reset-password') {
            history.replaceState({}, '', `/reset-password${hash}`);
        } else if (authHash.type !== 'recovery' && path !== '/confirm-email') {
            history.replaceState({}, '', `/confirm-email${hash}`);
        }
    } else if (authHash.error) {
        // Токена нет — Supabase вернул ошибку (ссылка просрочена/использована).
        // Запоминаем код и уводим на форму входа с уведомлением.
        sessionStorage.setItem('auth.notice', authHash.errorCode ?? authHash.error ?? 'error');
        history.replaceState({}, '', '/auth');
    }

    if (state.token && state.user) {
        try {
            const me = await apiRequest<{
                user: {
                    id: string;
                    email: string;
                    username: string | null;
                    is_moderator: boolean;
                    is_admin: boolean;
                    is_ghost: boolean;
                    player_id: number | null;
                    player_name: string | null;
                };
            }>('/api/auth/me', { token: state.token });

            saveSession(
                {
                    id: me.user.id,
                    email: me.user.email,
                    username: me.user.username,
                    is_moderator: me.user.is_moderator,
                    is_admin: me.user.is_admin,
                    is_ghost: me.user.is_ghost,
                    player_id: me.user.player_id ?? null,
                    player_name: me.user.player_name ?? null,
                },
                state.token
            );
        } catch (err) {
            // Сессию сбрасываем только если обновлять уже нечем: при валидном
            // refresh_token слой api сам повторит запрос (а при явном отказе — очистит сессию).
            if (err instanceof ApiRequestError && err.status === 401 && !state.refreshToken) {
                clearSession();
            }
        }
    }

    setupApp();

    registerRoute('/', { mount: mountPlayers, unmount: unmountPlayers });
    registerRoute('/games', { mount: mountGames, unmount: unmountGames });
    registerRoute('/control', { mount: mountControl, unmount: unmountControl });
    registerRoute('/admin', { mount: mountAdmin, unmount: unmountAdmin });
    registerRoute('/auth', { mount: mountAuth, unmount: unmountAuth });
    registerRoute('/reset-password', {
        mount: mountResetPassword,
        unmount: unmountResetPassword,
    });
    registerRoute('/confirm-email', {
        mount: mountConfirmEmail,
        unmount: unmountConfirmEmail,
    });
    registerRoute('/leaderboard', {
        mount: mountLeaderboard,
        unmount: unmountLeaderboard,
    });
    registerRoute('/maps', { mount: mountMaps, unmount: unmountMaps });
    registerRoute('/mods', { mount: mountMods, unmount: unmountMods });
    registerRoute('/tools', { mount: mountTools, unmount: unmountTools });
    registerRoute('/stats', { mount: mountStatistics, unmount: unmountStatistics });
    registerRoute('/tools/predict', {
        mount: mountPredict,
        unmount: unmountPredict,
    });
    registerRoute('/tools/random', { mount: mountRandom, unmount: unmountRandom });
    registerRoute('/tools/match', { mount: mountMatch, unmount: unmountMatch });
    registerRoute('/support', { mount: mountSupport, unmount: unmountSupport });
    registerRoute('/profile', { mount: mountProfile, unmount: unmountProfile });
    registerRoute('/my-stats', { mount: mountMyStats, unmount: unmountMyStats });
    registerRoute('/archetypes', { mount: mountArchetypes, unmount: unmountArchetypes });

    setNotFoundHandler({
        mount: () => {
            window.history.replaceState({}, '', '/');
            void mountPlayers(new URLSearchParams());
        },
    });

    initVisitTracking();
    initRouter();
    markPageReady();

    // «С прошлого визита» — необязательный бонус, грузим после первой отрисовки.
    void initRecap();
}

void bootstrap();